import type { ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import {
  useCourse,
  useCourseDescription,
  useCourseLearningOutcomes,
  useCourseTableOfContent,
} from '@openclassrooms/data-connect-web/learning-content';
import {
  useUserCourseLearningOutcomes,
  useUserCourseTableOfContent,
  useUserCourse,
} from '@openclassrooms/data-connect-web/learning-activity';
import { CourseStatus } from '@openclassrooms/sdk-web';
import { getFlatToc } from '@openclassrooms/ui-web/table-of-content';

import CourseContext from './courseContext';
import { CourseType, PartType } from '../types';
import { useErrorHandlers } from './errorHandlerContext';
import jsonConfig from '../_temporary/jsonConfig';
import getNextPartFromToc from '../services/getNextPartFromToc';

export interface CourseContextProviderProps {
  children: ReactNode;
}

export default function CourseContextProvider({ children }: CourseContextProviderProps) {
  const urlParams = useParams<{ courseIdSlug: string }>();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const courseStatus = searchParams.get('status') as CourseStatus | undefined;
  const { onError } = useErrorHandlers();
  const { courseIdSlug } = urlParams;
  const config = jsonConfig('headerConfiguration');
  const canEditCourse = config?.right?.canEditCourse;
  const [courseIdParam] = courseIdSlug?.split(/-(.+)/);
  const courseId = Number(courseIdParam);

  const { currentUser, userId, isLoading: isLoadingUser } = useCurrentUserContext();

  const {
    data: userCourseData,
    error: userCourseError,
    refetch: refetchUserCourseData,
  } = useUserCourse(
    {
      userId: userId!,
      courseId,
      queryParams: {
        status: courseStatus,
      },
    },
    { enabled: Boolean(userId) && !isLoadingUser }
  );

  const {
    data: courseData,
    error: courseError,
    refetch: refetchCourseData,
  } = useCourse(
    {
      id: courseId,
    },
    { enabled: Boolean(!userId && !isLoadingUser) }
  );

  const course = (userCourseData || courseData) as CourseType;

  const {
    data: learningOutcomes,
    error: learningOutcomeError,
    isInitialLoading: isLoadingOutcomes,
  } = useCourseLearningOutcomes(
    {
      courseId,
    },
    { enabled: Boolean(!userId && !isLoadingUser) }
  );

  const {
    data: userLearningOutcomes,
    error: userLearningOutcomeError,
    isInitialLoading: isLoadingUserLearningOutcomes,
  } = useUserCourseLearningOutcomes(
    {
      courseId,
      userId,
      queryParams: {
        status: courseStatus,
      },
    },
    { enabled: Boolean(userId && !isLoadingUser) }
  );

  const { data: description, error: descriptionError } = useCourseDescription({
    courseId,
    queryParams: {
      status: courseStatus,
    },
  });

  const {
    data: userToc,
    error: userTocError,
    refetch: refetchUserTocData,
    isInitialLoading: isLoadingUserToc,
  } = useUserCourseTableOfContent(
    {
      userId,
      courseId,
      queryParams: {
        status: courseStatus,
      },
    },
    { enabled: Boolean(userId && !isLoadingUser && course && !course.isSinglePage) }
  );

  const {
    data: courseToc,
    error: courseTocError,
    refetch: refetchCourseTocData,
    isInitialLoading: isLoadingCourseToc,
  } = useCourseTableOfContent(
    {
      courseId,
    },
    {
      enabled: Boolean(!userId && !isLoadingUser && course && !course.isSinglePage),
    }
  );

  const tableOfContent = (userToc || courseToc) as PartType;
  const tocError = userTocError || courseTocError;
  const flatToc = getFlatToc(tableOfContent || {});

  const error =
    courseError ||
    userCourseError ||
    tocError ||
    descriptionError ||
    learningOutcomeError ||
    userLearningOutcomeError;

  if (error) {
    onError(error);
    return null;
  }

  const nextChapter = userToc && course ? getNextPartFromToc(flatToc, course) : undefined;

  return (
    <CourseContext.Provider
      value={{
        currentUser,
        courseId,
        course,
        refetchTocData: refetchUserTocData || refetchCourseTocData,
        refetchCourseData: refetchUserCourseData || refetchCourseData,
        userToc: tableOfContent,
        flatToc,
        nextChapter,
        description,
        isLoadingUserToc: isLoadingUserToc || isLoadingCourseToc,
        canEditCourse,
        learningOutcomes: learningOutcomes || userLearningOutcomes,
        isLoadingLearningOutcomes: isLoadingOutcomes || isLoadingUserLearningOutcomes,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
}
